<script setup lang="ts">
import { AbcDropdown } from "@ordbogen-npm/abc-dsk";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import TgSavedToast from "@/abc-dsk/components/SavedToast/TgSavedToast.vue";
import InstitutionsService from "@/common/api/institutions.service";
import { MatomoHelper } from "@/helpers";
import { useAuthStore, useSettingsStore } from "@/stores";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

const { t } = useI18n();

const authStore = useAuthStore();
const settingsStore = useSettingsStore();

const primaryInstitutionId = computed<string>(() => authStore.user.primaryInstitutionId ?? "");

const primaryInstitutionSavedToast = ref<boolean>(false);
const institutionListUpdatedToast = ref<boolean>(false);

const options = computed(() =>
  authStore.user.institutions.map((institution) => ({
    value: institution.id,
    label: institution.name
  }))
);

const setInstitution = async (institutionId: string) => {
  if (institutionId === primaryInstitutionId.value) {
    return;
  }

  primaryInstitutionSavedToast.value = false;
  await authStore.updatePrimaryInstitution(institutionId);
  settingsStore.setActiveLanguageGroup(undefined);

  primaryInstitutionSavedToast.value = true;

  MatomoHelper.sendEvent(
    MatomoEventCategory.AbcSettings,
    MatomoEventAction.InstitutionsSettings,
    "Switched institution"
  );
};

const fetchInstitutions = async () => {
  institutionListUpdatedToast.value = false;

  const response = await InstitutionsService.list();
  const institutions = response.data.data;

  // If the institution list still contains the current primary institution, simply update the
  // list. Otherwise, we select the first institution in the list as the new primary institution.
  if (institutions.some((institution) => institution.id === primaryInstitutionId.value)) {
    authStore.setInstitutions(institutions);
  } else {
    await authStore.updatePrimaryInstitution(institutions[0].id);
  }

  institutionListUpdatedToast.value = true;

  MatomoHelper.sendEvent(
    MatomoEventCategory.AbcSettings,
    MatomoEventAction.InstitutionsSettings,
    "Updated institution list"
  );
};

const dropdownMarginPx = computed<string>(() => {
  const relevantOptions = options.value.length - 3;
  if (relevantOptions <= 0) {
    return "0";
  }

  return `${relevantOptions * 60}px`;
});
</script>

<template>
  <div>
    <div class="d-none d-lg-block settings-group">
      <h2 class="settings-group__heading settings-group__heading--big">
        {{ t("settings.institutions.title") }}
      </h2>
    </div>

    <hr class="settings-group-divider d-none d-lg-block" />

    <div class="settings-group row">
      <div class="col-12 settings-group__content">
        <div class="settings-group__details">
          <h3 class="settings-group__heading">{{ t("settings.institutions.heading") }}</h3>
          <p class="settings-group__subheading">
            {{ t("settings.institutions.description") }}
          </p>
        </div>
      </div>
      <div class="col-12">
        <div class="settings-group__toast-container-vertical">
          <AbcDropdown
            :model-value="primaryInstitutionId"
            :options="options"
            :aria-label="t('settings.institutions.heading')"
            @change="setInstitution($event)"
          />
          <TgSavedToast
            v-model:show="primaryInstitutionSavedToast"
            class="ml-2 mt-2 mb-n2 ml-lg-3 mb-lg-0 mt-lg-0"
          />
          <TgSavedToast
            v-model:show="institutionListUpdatedToast"
            :text="t('settings.institutions.update.toast')"
            class="ml-2 mt-2 mb-n2 ml-lg-3 mb-lg-0 mt-lg-0"
          />
        </div>
      </div>
    </div>

    <hr class="settings-group-divider" />

    <div class="settings-group row">
      <div class="col-12 settings-group__content">
        <div class="settings-group__details">
          <p class="settings-group__subheading">
            {{ t("settings.institutions.update.description") }}
          </p>
        </div>
      </div>
      <div class="col-12">
        <div class="margin-for-dropdown">
          <TgButton variant="outline" size="sm" color="secondary" @click="fetchInstitutions()">
            {{ t("settings.institutions.update.button") }}
          </TgButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
.margin-for-dropdown {
  margin-bottom: v-bind("dropdownMarginPx");
}
</style>
